import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus, Filter, LayoutGrid, Table } from 'lucide-react';
import { Class } from '@/types/classTypes';
import { DataViewScaffold } from '@/components/core';
import ClassCard from './ClassCard';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LocationFilter from './filters/LocationFilter';
import SeasonFilter from './filters/SeasonFilter';
import { useOrganizationCoreSettings } from '@/hooks/useOrganizationCoreSettings';

interface ClassListProps {
  organizationId: string;
  classes: Class[];
  loading: boolean;
  error: Error | null;
}

const ClassList: React.FC<ClassListProps> = ({ 
  organizationId, 
  classes, 
  loading, 
  error 
}) => {
  const [displayMode, setDisplayMode] = useState<'all' | 'classes' | 'events'>('all');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  
  const { organization } = useOrganizationCoreSettings(organizationId);
  
  const filteredClasses = classes.filter(cls => {
    // First apply type filter (class or event)
    const passesTypeFilter = 
      displayMode === 'all' ||
      (displayMode === 'classes' && !cls.is_event) ||
      (displayMode === 'events' && cls.is_event);
    
    // Then apply location filter if selected
    const passesLocationFilter = !selectedLocationId || 
      cls.location_id === selectedLocationId;
    
    // Apply season filter if selected
    const passesSeasonFilter = !selectedSeasonId || 
      (selectedSeasonId === 'none' && !cls.season_id) || 
      cls.season_id === selectedSeasonId;
      
    return passesTypeFilter && passesLocationFilter && passesSeasonFilter;
  });
  
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <Tabs 
          value={displayMode} 
          onValueChange={(value) => setDisplayMode(value as 'all' | 'classes' | 'events')}
          className="w-auto"
        >
          <TabsList className="grid w-[300px] grid-cols-3">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="classes">Classes</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex flex-wrap gap-2">
          <LocationFilter 
            selectedLocationId={selectedLocationId}
            onLocationChange={setSelectedLocationId}
          />
          
          <SeasonFilter
            selectedSeasonId={selectedSeasonId}
            onSeasonChange={setSelectedSeasonId}
          />
        </div>
        
        <div className="flex gap-2 ml-auto">
          <Link to="/classes/new">
            <Button variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Add Class
            </Button>
          </Link>
          <Link to="/classes/new-event">
            <Button>
              <Plus className="h-4 w-4 mr-1" />
              Add Event
            </Button>
          </Link>
        </div>
      </div>
      <DataViewScaffold
        items={filteredClasses}
        loading={loading}
        views={['card', 'table']}
        defaultView="card"
        storageKey="classes-view"
        cardColumns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
        cardGap={6}
        renderCard={(cls) => (
          <ClassCard
            key={cls.id}
            classData={cls}
            parentCanSelfEnrollTrial={organization?.parent_can_self_enroll_trial}
          />
        )}
        renderTableHeader={() => (
          <tr>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Location</th>
            <th className="px-4 py-2 text-left">Actions</th>
          </tr>
        )}
        renderTableRow={(cls) => (
          <>
            <td className="px-4 py-2 font-medium">{cls.name}</td>
            <td className="px-4 py-2">
              {cls.is_active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Active</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold">Inactive</span>
              )}
            </td>
            <td className="px-4 py-2">
              {cls.is_event ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">Event</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">Class</span>
              )}
            </td>
            <td className="px-4 py-2">{cls.locations?.name || cls.location || '-'}</td>
            <td className="px-4 py-2">
              <Link to={`/admin/classes?classId=${cls.id}`} className="text-primary underline">View</Link>
            </td>
          </>
        )}
        emptyState={loading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-500">Error loading classes: {error.message}</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">No classes found.</div>
        )}
      />
    </div>
  );
};

export default ClassList;
